import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber, type Decimal } from '@/lib/decimal'
import { formatContractHours, formatHours } from '@/lib/format'
import { cn } from '@/lib/utils'

interface UtilizationBarProps {
  hours: Decimal
  contractHours: Decimal | null
  percentage: number | null
  align?: 'left' | 'right'
  contractTitle?: string
  className?: string
  barClassName?: string
}

/**
 * Gedeeld A/B-component: het mono cijferpaar "28,0 / 36" met een dunne
 * voortgangslijn. Zonder contract toont het contractcijfer een en-dash en
 * blijft de voortgangslijn leeg.
 */
export function UtilizationBar({
  hours,
  contractHours,
  percentage,
  align = 'left',
  contractTitle,
  className,
  barClassName,
}: UtilizationBarProps) {
  const width = percentage != null ? Math.min(100, Math.max(0, percentage)) : 0

  return (
    <span className={cn('flex flex-col gap-1', className)}>
      <span className={cn('flex items-baseline gap-1 text-sm', align === 'right' && 'justify-end')}>
        <span className="money text-ink">{formatHours(toNumber(hours))}</span>
        {contractHours != null ? (
          <Tooltip content={contractTitle}>
            <span className={cn('money text-ink-faint', contractTitle && 'cursor-help border-b border-dotted border-ink-faint/50')}>
              / {formatContractHours(toNumber(contractHours))}
            </span>
          </Tooltip>
        ) : (
          <Tooltip content="Geen contracturen (flexibel)">
            <span className="money cursor-help text-ink-faint" aria-label="Flexibel, geen contracturen">
              / &#x2013;
            </span>
          </Tooltip>
        )}
      </span>
      <span className={cn('block h-0.5 w-full overflow-hidden rounded-full bg-fill-1', barClassName)}>
        {percentage != null ? (
          <span
            className="block h-full rounded-full bg-accent-600 transition-[width] duration-500 ease-spring"
            style={{ width: `${width}%` }}
          />
        ) : null}
      </span>
    </span>
  )
}
